// LiveNowPill — friends-live card surfaced on Home, sits above the bottom nav.
// Ported from design-canon/project/src/screens-flow.jsx HomeScreen → .friends-live-card
// (lines 175-199, styles 270-327). Inlined as a self-contained component so Home
// doesn't grow another <style> block.
//
// Renders nothing when 0 friends are live — caller can stay agnostic.
// Headline: "{topFriend.name} at {topFriend.venue}" (top by most recent startedAt).
// Sub:      "+{N} more out tonight" when more than 1 friend live.
// Tap target: whole card, navigates to /stumbling.
//
// Avatar stack: top 3 friends (most recent first), colored chips overlapping
// left-to-right. A teal pulse dot sits at the bottom-right of the cluster.

import { Link } from '@tanstack/react-router'
import type { ActiveStumbleDto } from '../api'
import { Icon } from './Icon'

// Same canon palette used elsewhere (LiveBoard, history) so the same friend
// keeps the same color across screens once we map by ownerId in a later pass.
const AVATAR_COLORS = [
  'var(--pink)',
  'var(--lime)',
  'var(--teal)',
  'var(--orange)',
  'var(--plum)',
  'var(--acid)',
]

function initialOf(name: string): string {
  const trimmed = (name ?? '').trim()
  return trimmed.length > 0 ? trimmed.charAt(0).toUpperCase() : '?'
}

export function LiveNowPill({ stumbles }: { stumbles: ActiveStumbleDto[] }) {
  if (stumbles.length === 0) return null

  // Sort by recency — newest started first. We want the freshest face on top.
  const sorted = [...stumbles].sort(
    (a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime(),
  )
  // Length > 0 guaranteed by the early-return guard above, but narrow for TS.
  const top = sorted[0]
  if (!top) return null
  const top3 = sorted.slice(0, 3)
  const more = stumbles.length - 1

  const headlineVenue = top.currentVenue?.name ?? 'somewhere'

  return (
    <Link
      to="/stumbling"
      className="flc-card"
      data-testid="home-live-now-pill"
      aria-label={`See friends' stumbles — ${top.ownerDisplayName} at ${headlineVenue}${more > 0 ? `, ${more} more out tonight` : ''}`}
    >
      <div className="flc-stack">
        <div className="flc-avatars">
          {top3.map((s, i) => (
            <span
              key={s.ownerId}
              className="flc-avatar"
              style={{
                background: AVATAR_COLORS[i % AVATAR_COLORS.length],
                marginLeft: i === 0 ? 0 : -10,
                zIndex: 10 - i,
              }}
              aria-hidden
            >
              {initialOf(s.ownerDisplayName)}
            </span>
          ))}
          <span className="flc-pulse" aria-hidden />
        </div>
        <div className="flc-meta">
          <div className="eyebrow" style={{ fontSize: 9 }}>
            live now
          </div>
          <div className="flc-title">
            <strong>{top.ownerDisplayName}</strong>{' '}
            <span style={{ opacity: 0.7 }}>at {headlineVenue}</span>
          </div>
          {more > 0 && (
            <div className="body-sm muted" style={{ fontSize: 12 }}>
              +{more} more out tonight
            </div>
          )}
        </div>
      </div>
      <span className="flc-arrow" aria-hidden>
        {/* canon uses the back chevron mirrored — points right toward the route */}
        <Icon name="back" size={18} color="var(--ink)" />
      </span>

      <style>{`
        .flc-card {
          display: grid;
          grid-template-columns: 1fr auto;
          align-items: center;
          gap: 12px;
          padding: 10px 12px;
          border: 2.5px solid var(--line);
          border-radius: 16px;
          background: var(--paper);
          box-shadow: 0 5px 0 var(--shadow-ink);
          text-align: left;
          color: var(--ink);
          min-height: 0;
        }
        .flc-card:active {
          transform: translateY(2px);
          box-shadow: 0 3px 0 var(--shadow-ink);
        }
        .flc-stack {
          display: grid;
          grid-template-columns: auto 1fr;
          gap: 10px;
          align-items: center;
          min-width: 0;
        }
        .flc-avatars {
          position: relative;
          display: flex;
          align-items: center;
        }
        .flc-avatar {
          width: 38px;
          height: 38px;
          border-radius: 999px;
          color: var(--paper);
          border: 2.5px solid var(--line);
          display: grid;
          place-items: center;
          font-family: var(--font-display);
          font-size: 16px;
        }
        .flc-pulse {
          position: absolute;
          bottom: -3px;
          right: -3px;
          width: 12px;
          height: 12px;
          border-radius: 999px;
          background: var(--teal);
          border: 2.5px solid var(--paper);
          box-shadow: 0 0 0 0 rgba(51, 183, 199, 0.7);
          animation: flc-ping 1.6s ease-out infinite;
        }
        @keyframes flc-ping {
          0%   { box-shadow: 0 0 0 0 rgba(51, 183, 199, 0.55); }
          70%  { box-shadow: 0 0 0 10px rgba(51, 183, 199, 0); }
          100% { box-shadow: 0 0 0 0 rgba(51, 183, 199, 0); }
        }
        .flc-meta {
          display: grid;
          gap: 1px;
          min-width: 0;
        }
        .flc-title {
          font-family: var(--font-display);
          font-size: 20px;
          line-height: 1;
          text-transform: lowercase;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .flc-arrow {
          width: 36px;
          height: 36px;
          border-radius: 999px;
          background: var(--teal);
          border: 2px solid var(--line);
          display: grid;
          place-items: center;
          transform: scaleX(-1);
          flex-shrink: 0;
        }
        @media (prefers-reduced-motion: reduce) {
          .flc-pulse { animation: none; }
        }
      `}</style>
    </Link>
  )
}
